import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useEffect, useRef, useState } from 'react';

import { LocationNodeData } from '../../../../../@types';
import { PartLocationObject, SearchCode } from '../../../../../@types/part.type';
import { useSearchNodesParts } from '../../../../../queries/live-search-query';
import { displayNodeTypeName } from '../../../../../utils/node-type';

export interface EditablePartLocationResult {
  locationId: number;
  locationTypeCode: string;
  locationHierarchy: string;
}

export interface EditablePartLocationProps {
  partLocation?: PartLocationObject;
  isEditable: boolean;
  onSave: (result: EditablePartLocationResult) => void;
}

const resolveLocationSelection = (option: LocationNodeData): EditablePartLocationResult => {
  if (option.name?.includes(SearchCode.AM_PID)) {
    return {
      locationId: option.id,
      locationTypeCode: 'part',
      locationHierarchy: option.locationHierarchy || '',
    };
  }
  if (option.name?.includes(SearchCode.AM_LID)) {
    return {
      locationId: option.id,
      locationTypeCode: 'node',
      locationHierarchy: option.parentTree || '',
    };
  }
  const isPart = Boolean(option.serial1 || option.isSerialised);
  return {
    locationId: option.id,
    locationTypeCode: isPart ? 'part' : 'node',
    locationHierarchy: option.parentTree || '',
  };
};

const buildDisplay = (partLocation?: PartLocationObject): string => {
  if (!partLocation) return '';
  if (partLocation.locationTree) return partLocation.locationTree;
  const names = partLocation.nodeList?.map((n) => n.name).filter(Boolean) ?? [];
  if (names.length > 0) return names.join(' / ');
  return partLocation.name ?? '';
};

export const EditablePartLocation: React.FC<EditablePartLocationProps> = ({
  partLocation,
  isEditable,
  onSave,
}) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<LocationNodeData[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [selected, setSelected] = useState<LocationNodeData | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState(false);

  const searchNodesParts = useSearchNodesParts();

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  const handleOpen = () => {
    setInputValue('');
    setOptions([]);
    setSelected(null);
    setSearchError(false);
    setOpen(true);
  };

  const handleCancel = () => setOpen(false);

  const handleSave = () => {
    if (!selected) {
      setOpen(false);
      return;
    }
    onSave(resolveLocationSelection(selected));
    setOpen(false);
  };

  const handleInputChange = (_: unknown, next: string) => {
    setInputValue(next);
    if (timerRef.current) clearTimeout(timerRef.current);
    const term = next.trim();
    if (term.length < 3 || (term.toLowerCase().includes('am-') && term.length < 8)) {
      setOptions([]);
      setLoading(false);
      setSearchError(false);
      return;
    }
    setLoading(true);
    setSearchError(false);
    timerRef.current = setTimeout(async () => {
      try {
        const results = await searchNodesParts.mutateAsync({
          id: partLocation?.id ?? 0,
          name: term.replaceAll('#', '%23'),
          nodeIdTree: partLocation?.locationHierarchy ?? '',
          includeSiblings: true,
        });
        setOptions(results ?? []);
      } catch {
        setOptions([]);
        setSearchError(true);
      } finally {
        setLoading(false);
      }
    }, 400);
  };

  const displayText = buildDisplay(partLocation);

  return (
    <Box ref={anchorRef} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box component="span" sx={{ fontSize: '13px', lineHeight: 1.3 }}>
        {displayText || (
          <Box component="span" sx={{ color: 'text.disabled' }}>
            —
          </Box>
        )}
      </Box>
      {isEditable && (
        <IconButton
          size="small"
          onClick={handleOpen}
          aria-label="Edit Current Part Location"
          sx={{ p: 0.25, color: 'text.secondary', '&:hover': { color: 'v4.accent' } }}
        >
          <EditOutlinedIcon sx={{ fontSize: 14 }} />
        </IconButton>
      )}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        sx={{ zIndex: 1500 }}
      >
        <ClickAwayListener onClickAway={handleCancel}>
          <Paper
            elevation={8}
            sx={{
              p: 2,
              mt: 0.5,
              border: '1px solid',
              borderColor: 'v4.border',
              borderRadius: 2,
              width: 360,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Autocomplete<LocationNodeData, false, false, false>
                size="small"
                disablePortal
                options={options}
                value={selected}
                inputValue={inputValue}
                loading={loading}
                filterOptions={(opts) => opts}
                getOptionLabel={(opt) => opt?.name || ''}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                noOptionsText={
                  searchError
                    ? 'Search failed — try again'
                    : inputValue
                      ? 'No results'
                      : 'Type 3+ characters to search'
                }
                onInputChange={handleInputChange}
                onChange={(_, next) => setSelected(next)}
                renderOption={(props, option) => (
                  <li {...props} key={`${option.id}-${option.code}`}>
                    <Box>
                      <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
                        {option.name}
                      </Typography>
                      <Typography sx={{ fontSize: '10px', color: 'text.secondary' }}>
                        {displayNodeTypeName(option.nodeTypeName)}
                        {option.parentTree ? ` · ${option.parentTree}` : ''}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    // eslint-disable-next-line jsx-a11y/no-autofocus -- opens on user intent
                    autoFocus
                    placeholder="Search location or part"
                    inputProps={{ ...params.inputProps, 'aria-label': 'Search location or part' }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading && <CircularProgress size={14} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px' } }}
                  />
                )}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Box
                  component="button"
                  type="button"
                  onClick={handleCancel}
                  sx={{
                    fontSize: '11px',
                    px: 1.5,
                    py: 0.5,
                    border: 'none',
                    bgcolor: 'transparent',
                    cursor: 'pointer',
                    color: 'text.secondary',
                    fontFamily: 'inherit',
                  }}
                >
                  Cancel
                </Box>
                <Box
                  component="button"
                  type="button"
                  onClick={handleSave}
                  disabled={!selected}
                  sx={{
                    fontSize: '11px',
                    fontWeight: 600,
                    px: 1.5,
                    py: 0.5,
                    border: 'none',
                    borderRadius: '4px',
                    bgcolor: 'v4.accent',
                    color: 'common.white',
                    cursor: selected ? 'pointer' : 'not-allowed',
                    opacity: selected ? 1 : 0.5,
                    fontFamily: 'inherit',
                    '&:hover': { opacity: selected ? 0.9 : 0.5 },
                    '&:disabled': { cursor: 'not-allowed', opacity: 0.5 },
                  }}
                >
                  Save
                </Box>
              </Box>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};
